/**
 * OsPinEntry — OS-INV-10
 *
 * Route: /os/pin (public — no Firebase or session gate)
 *
 * UX rationale: A warehouse floor PIN entry. Big numeric keypad = easy
 * one-thumb input with gloves; 8 masked dots give tactile feedback without
 * exposing the PIN over someone's shoulder. Auto-submit on 8th digit removes
 * the need for a "Submit" tap (saves one step for the most common path).
 * Lockout state disables input and shows a live countdown so the worker
 * knows when to retry — not just "something is wrong."
 *
 * API contract (OS-INV-09 — locked):
 *   POST /api/os/session
 *   Body: { pin: "8-digit string", deviceId: "uuid" }
 *   201 → { token, expiresAt }
 *   401 → { error: "Invalid PIN" }
 *   429 → { error, cooldownUntil }
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import { useOsSession } from '../hooks/useOsSession';
import { useTenant } from '../../contexts/TenantContext';

const PIN_LENGTH = 8;

// Default post-PIN destination is the PIN-gated scan entry screen — NOT
// /os/inventory, which is Firebase-staff-gated and would bounce a PIN-only
// device straight back to login (looking like "nothing happened").
const DEFAULT_NEXT = '/os/scan';

// Staff-only (Firebase-gated) OS routes a PIN device must never be sent to.
const STAFF_ONLY_PREFIXES = ['/os/inventory', '/os/count-sheet'];

function sanitizeNext(raw: string | null): string {
  if (!raw) return DEFAULT_NEXT;
  let decoded: string;
  try { decoded = decodeURIComponent(raw); } catch { return DEFAULT_NEXT; }
  // Must be an /os/ path, and must not be a Firebase-staff-gated page.
  if (!decoded.startsWith('/os/')) return DEFAULT_NEXT;
  if (STAFF_ONLY_PREFIXES.some(p => decoded === p || decoded.startsWith(p + '/'))) {
    return DEFAULT_NEXT;
  }
  return decoded;
}

function formatCountdown(targetIso: string): string {
  const diffMs = new Date(targetIso).getTime() - Date.now();
  if (diffMs <= 0) return '0 minutes';
  const totalSec = Math.ceil(diffMs / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  if (mins === 0) return `${secs} second${secs !== 1 ? 's' : ''}`;
  if (secs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
  return `${mins}m ${secs}s`;
}

type PinState = 'idle' | 'loading' | 'error' | 'locked' | 'network_error';

export const OsPinEntry: React.FC = () => {
  const { apiBase } = useTenant();
  const { getSnapshot, storeSession, hasStaleToken } = useOsSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = sanitizeNext(searchParams.get('next'));

  const [digits, setDigits] = useState<string>('');
  const [state, setState] = useState<PinState>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [cooldownUntil, setCooldownUntil] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<string>('');
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check if already authenticated — redirect immediately, no PIN form flicker
  useEffect(() => {
    const { isAuthenticated } = getSnapshot();
    if (isAuthenticated) {
      navigate(nextPath, { replace: true });
    }
  }, []);  

  // Live countdown when locked out
  useEffect(() => {
    if (!cooldownUntil) return;
    const tick = () => {
      const remaining = new Date(cooldownUntil).getTime() - Date.now();
      if (remaining <= 0) {
        setCooldownUntil(null);
        setState('idle');
        setDigits('');
        if (cooldownRef.current) clearInterval(cooldownRef.current);
      } else {
        setCountdown(formatCountdown(cooldownUntil));
      }
    };
    tick();
    cooldownRef.current = setInterval(tick, 1000);
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, [cooldownUntil]);

  const submitPin = useCallback(async (pin: string) => {
    setState('loading');
    setErrorMsg('');
    const { deviceId } = getSnapshot();
    try {
      const res = await fetch(`${apiBase}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, deviceId }),
      });

      if (res.status === 201) {
        const data: { token: string; expiresAt: string } = await res.json();
        storeSession(data.token, data.expiresAt);
        navigate(nextPath, { replace: true });
        return;
      }

      if (res.status === 401) {
        setState('error');
        setDigits('');
        setErrorMsg('Incorrect PIN — please try again.');
        return;
      }

      if (res.status === 429) {
        const data: { error: string; cooldownUntil: string } = await res.json();
        setCooldownUntil(data.cooldownUntil);
        setState('locked');
        setDigits('');
        return;
      }

      // Unexpected server error
      setState('error');
      setErrorMsg('Something went wrong. Please try again.');
      setDigits('');
    } catch {
      setState('network_error');
      setErrorMsg('Network error — check your connection and retry.');
      setDigits('');
    }
  }, [apiBase, getSnapshot, storeSession, navigate, nextPath]);

  const appendDigit = useCallback((d: string) => {
    if (state === 'locked' || state === 'loading') return;
    setDigits(prev => {
      const next = prev.length < PIN_LENGTH ? prev + d : prev;
      if (next.length === PIN_LENGTH) {
        // auto-submit after state update propagates
        setTimeout(() => submitPin(next), 0);
      }
      return next;
    });
    if (state === 'error' || state === 'network_error') {
      setState('idle');
      setErrorMsg('');
    }
  }, [state, submitPin]);

  const backspace = useCallback(() => {
    if (state === 'locked' || state === 'loading') return;
    setDigits(prev => prev.slice(0, -1));
  }, [state]);

  const retryNetwork = useCallback(() => {
    setState('idle');
    setErrorMsg('');
    setDigits('');
  }, []);

  const isLocked   = state === 'locked';
  const isLoading  = state === 'loading';
  const isDisabled = isLocked || isLoading;
  const showStale  = hasStaleToken();

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#001550',
        p: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: 380,
          p: 3,
          borderRadius: 3,
          bgcolor: '#ffffff',
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: '#001550', letterSpacing: '0.02em' }}
          >
            Offshore Sails
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Device Login
          </Typography>
        </Box>

        {/* Contextual messages */}
        {showStale && state === 'idle' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your session has expired — please enter your PIN again.
          </Alert>
        )}

        {state === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}

        {state === 'network_error' && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button size="small" onClick={retryNetwork}>
                Retry
              </Button>
            }
          >
            {errorMsg}
          </Alert>
        )}

        {isLocked && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Too many failed attempts — try again in{' '}
            <strong>{countdown}</strong>.
          </Alert>
        )}

        {/* PIN dot display */}
        <Box
          aria-label="PIN entry"
          role="group"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
            mb: 3,
          }}
        >
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: isLocked ? 'error.main' : '#001550',
                bgcolor: i < digits.length
                  ? (isLocked ? 'error.main' : '#001550')
                  : 'transparent',
                transition: 'background-color 0.1s ease',
              }}
            />
          ))}
        </Box>

        {/* Loading indicator */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {/* Numeric keypad */}
        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          {['1','2','3','4','5','6','7','8','9'].map(d => (
            <Grid item xs={4} key={d}>
              <Button
                fullWidth
                variant="outlined"
                disabled={isDisabled}
                onClick={() => appendDigit(d)}
                aria-label={`Digit ${d}`}
                sx={{
                  height: 64,
                  fontSize: '1.5rem',
                  fontWeight: 500,
                  borderColor: '#ccc',
                  color: '#001550',
                  borderRadius: 2,
                  '&:hover': { borderColor: '#001550', bgcolor: 'rgba(0,21,80,0.06)' },
                  '&:active': { bgcolor: 'rgba(0,21,80,0.14)' },
                }}
              >
                {d}
              </Button>
            </Grid>
          ))}
          {/* Bottom row: empty, 0, backspace */}
          <Grid item xs={4} />
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              disabled={isDisabled}
              onClick={() => appendDigit('0')}
              aria-label="Digit 0"
              sx={{
                height: 64,
                fontSize: '1.5rem',
                fontWeight: 500,
                borderColor: '#ccc',
                color: '#001550',
                borderRadius: 2,
                '&:hover': { borderColor: '#001550', bgcolor: 'rgba(0,21,80,0.06)' },
                '&:active': { bgcolor: 'rgba(0,21,80,0.14)' },
              }}
            >
              0
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              disabled={isDisabled}
              onClick={backspace}
              aria-label="Backspace"
              sx={{
                height: 64,
                borderColor: '#ccc',
                color: '#001550',
                borderRadius: 2,
                '&:hover': { borderColor: '#001550', bgcolor: 'rgba(0,21,80,0.06)' },
                '&:active': { bgcolor: 'rgba(0,21,80,0.14)' },
              }}
            >
              <BackspaceIcon />
            </Button>
          </Grid>
        </Grid>

        {/* Validation hint */}
        {digits.length > 0 && digits.length < PIN_LENGTH && !isLoading && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 1 }}
            role="status"
          >
            {PIN_LENGTH - digits.length} more digit{PIN_LENGTH - digits.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};
